import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkle, MapPin } from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import ListenButton from "@/components/ListenButton";

/**
 * FeelThePulseStory — the full editorial version of "Feel the Pulse of Nature".
 * Long-form serif article. Rendered at /pulse/story.
 */

// Little helper — a section heading with an accent rule
const H2 = ({ id, children }) => (
  <h2 id={id} className="font-serif-display text-2xl md:text-4xl leading-[1.05] tracking-tight text-[#151312] scroll-mt-28">
    <span className="inline-block border-b-2 border-[color:var(--np-brand,#B85F2A)]/40 pb-1">{children}</span>
  </h2>
);

const Section = ({ id, title, children }) => (
  <section id={id} className="mt-12 md:mt-16 first:mt-6">
    {title && <H2 id={id}>{title}</H2>}
    <div className="mt-4 space-y-3 text-[15px] md:text-[17px] leading-[1.75] text-[#3A3830]">
      {children}
    </div>
  </section>
);

const List = ({ items }) => (
  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
    {items.map((line, i) => (
      <li key={i} className="text-[15px] leading-[1.55] text-[#3A3830]">{line}</li>
    ))}
  </ul>
);

const Callout = ({ children }) => (
  <div className="my-6 rounded-2xl bg-[#F5EBDC] border border-black/10 px-5 py-4 md:px-6 md:py-5">
    <div className="font-serif-display text-lg md:text-xl italic text-[#151312] leading-snug">{children}</div>
  </div>
);

export default function FeelThePulseStory() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

  return (
    <main
      className="bg-[color:var(--np-bg,#F5EBDC)] text-[#151312] pt-32 md:pt-36 pb-24 min-h-screen"
      data-testid="feel-the-pulse-story"
    >
      {/* Hero */}
      <header className="max-w-[860px] mx-auto px-5 md:px-10">
        <Link
          to="/pulse"
          data-testid="feel-the-pulse-back"
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted,#3A3830)] hover:text-[#151312]"
        >
          <ArrowLeft size={11} weight="bold" /> Back to Pulse
        </Link>
        <div className="mt-4 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-2">
          <Sparkle size={11} weight="fill" /> Nature Pulse · Manifesto
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-6xl leading-[0.98] tracking-tighter">
          Feel the Pulse of Nature
        </h1>
        <p className="mt-4 text-[#3A3830] text-base md:text-lg leading-relaxed">
          Nature is always changing. Individually, these moments may seem small. Together, they are the pulse of nature.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <ShareButton
            title="Feel the Pulse of Nature — NaturePulse"
            text="A living record of our changing planet, told by the community."
            hashtags={["NaturePulse", "FeelThePulse"]}
            variant="chip"
            testid="feel-the-pulse-story-share"
          />
          <ListenButton
            rootSelector="#np-pulse-article"
            testid="feel-the-pulse-story-listen"
          />
          <Link
            to="/stories/new"
            data-testid="feel-the-pulse-story-cta-add"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#241A10]"
          >
            Add a Pulse <ArrowRight size={11} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Body */}
      <article id="np-pulse-article" className="max-w-[720px] mx-auto px-5 md:px-10 mt-10">
        {/* Opening — drop cap treatment */}
        <div className="text-[15px] md:text-[17px] leading-[1.75] text-[#3A3830]">
          <p className="[&::first-letter]:font-serif-display [&::first-letter]:text-[64px] [&::first-letter]:leading-[0.9] [&::first-letter]:float-left [&::first-letter]:mr-3 [&::first-letter]:mt-1 [&::first-letter]:text-[color:var(--np-brand,#B85F2A)]">
            A tree begins to flower. A flock of birds arrives earlier than expected. Butterflies appear in a garden. A river rises after heavy rain. A familiar pond begins to dry. A shoreline slowly retreats. A new animal is spotted in a neighborhood. An unusually hot afternoon becomes part of a longer heat wave.
          </p>
          <p className="mt-3">Individually, these moments may seem small.</p>
          <p className="mt-1 font-serif-display text-xl text-[#151312]">Together, they are the pulse of nature.</p>
        </div>

        <Section id="welcome" title="Welcome to NaturePulse">
          <p><strong>NaturePulse</strong> is a community-powered place to discover, observe, document and share what is happening in the natural world around us.</p>
          <p>The Pulse brings those observations together.</p>
          <p>Think of it as a living stream of nature — constantly updated by people, experts, researchers, organizations and trusted information sources from communities around the world.</p>
          <p className="mt-4">Here you may discover:</p>
          <List items={[
            "🌱 A plant flowering in Florida, USA or Kerala, India",
            "🐦 A migratory bird arriving in Oregon",
            "🐝 Pollinators returning to a neighborhood garden",
            "🌳 A community planting trees in Africa",
            "🐘 Wildlife crossing a road in India",
            "🐟 Changes in fish populations in a local river",
            "🌊 Tidal flooding along a coastal city",
            "🔥 A wildfire affecting forests and wildlife",
            "💧 A lake reaching unusually low levels",
            "⛈️ Severe weather developing somewhere in the world",
            "🔬 New scientific research explaining environmental change",
          ]} />
          <Callout>Every Pulse tells part of a larger story.</Callout>
        </Section>

        <Section id="what-is-a-pulse" title="What Is a NaturePulse?">
          <p>A <strong>Pulse</strong> is a short, timely contribution about nature or the environment.</p>
          <ul className="mt-2 space-y-1.5 list-none">
            <li>It might be something you personally observed.</li>
            <li>It might be a photograph.</li>
            <li>It might be a question.</li>
            <li>It might be an important research study.</li>
            <li>It might be a link to an environmental news report.</li>
            <li>It might be a warning about flooding, fire, pollution or another environmental hazard.</li>
            <li>It might simply be something beautiful that deserves to be shared.</li>
          </ul>
          <p>NaturePulse gives all of these contributions a common home and connects them to the appropriate topic, location, species and community.</p>
        </Section>

        <Section id="observe" title="Observe Something? Share It.">
          <p>NaturePulse begins with observation.</p>
          <p>You don't need to be a scientist or environmental expert. If you notice something interesting in nature, you can help document it.</p>
          <p className="mt-3">Share observations about:</p>
          <div className="mt-4 space-y-4">
            {[
              ["🌱 Plants & Vegetation", "Flowers, plants, vegetation, wildflowers, seasonal growth, invasive plants and unusual changes."],
              ["🌳 Trees & Forests", "Trees, forests, tree health, flowering, fruiting, forest loss, reforestation, disease and regeneration."],
              ["🌾 Agriculture & Edibles", "Crops, fruits, vegetables, herbs, farming conditions, harvests, pests, drought and changing growing seasons."],
              ["🐦 Birds & Nesting", "Bird sightings, migration, nesting, feeding, unusual species and population changes."],
              ["🦌 Animals & Wildlife", "Wildlife sightings, tracks, migration, habitat changes and interactions between wildlife and people."],
              ["🐝 Insects & Pollinators", "Bees, butterflies, moths, dragonflies, fireflies and other insects."],
              ["🐟 Fish & Aquatic Life", "Fish, frogs, turtles, shellfish, aquatic plants and changes in freshwater or marine ecosystems."],
              ["🌦️ Climate, Weather & Sky", "Rain, snow, clouds, storms, heat, drought, fog, sunrise, sunset and unusual atmospheric conditions."],
              ["🌊 Oceans & Shores", "Tides, beaches, waves, coastal erosion, mangroves, coral reefs and marine life."],
              ["💧 Lakes, Rivers & Floods", "Water levels, flooding, drought, river conditions, reservoirs, wetlands and water quality."],
              ["🪨 Land & Geological Changes", "Landslides, erosion, rock formations, sinkholes, earthquakes and volcanic activity."],
              ["⚠️ Hazards & Impact Events", "Wildfires, floods, storms, pollution, fallen trees, damaged habitats and other events affecting nature or communities."],
              ["🍂 Seasonal & Climate Changes", "Flowering dates, migrations, harvests, leaf changes, rainfall seasons, first frost, first snow and other recurring natural events."],
            ].map(([h, d]) => (
              <div key={h}>
                <div className="font-serif-display text-lg text-[#151312]">{h}</div>
                <div className="text-[15px] text-[#3A3830] leading-[1.6] mt-0.5">{d}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="more-than-observations" title="Share More Than Observations">
          <p>The Pulse should also be a place for ideas and conversation.</p>
          <p>Contributions may include:</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {[
              ["Observation", "Something you personally noticed."],
              ["Photo", "A photograph documenting nature, wildlife, weather or environmental change."],
              ["Video", "A short field observation or educational video."],
              ["Comment", "Your perspective on a NaturePulse topic."],
              ["Question", "Something you would like the community or an expert to help explain."],
              ["Suggestion", "An idea for protecting, restoring or better understanding nature."],
              ["Useful Link", "A link to an article, research paper, video, podcast, government resource or educational website."],
              ["Research", "A scientific study or research finding worth exploring."],
              ["News", "An important environmental development from a credible source."],
              ["Warning", "Information about a potential environmental hazard or rapidly changing situation."],
              ["Official Response", "Updates from governments, agencies, researchers or organizations."],
            ].map(([h, d]) => (
              <div key={h}>
                <div className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">{h}</div>
                <div className="text-[14.5px] text-[#3A3830] leading-[1.55] mt-0.5">{d}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="context" title="Add Context to What You Share">
          <p>A photograph becomes much more valuable when we know where and when it was taken.</p>
          <p>Whenever possible, a Pulse can include:</p>
          <List items={[
            "📍 Location",
            "📅 Date",
            "🕐 Time",
            "📷 Photo or Video",
            "🌱 Species or Subject",
            "🌦️ Weather Conditions",
            "📝 What You Observed",
            "🏷️ Topics and Tags",
            "🔗 Source or Reference",
          ]} />
          <Callout>You may not know everything. That's perfectly fine. Share what you know and allow the NaturePulse community to help identify or explain the rest.</Callout>
        </Section>

        <Section id="every-pulse" title="Every Pulse Has a Place">
          <p>One of the most important ideas behind NaturePulse is that information should not disappear into an endless social-media stream.</p>
          <p>Every contribution should become connected to the places and subjects it describes.</p>
          <p className="mt-3">For example, a Pulse about a mangrove restoration project near Kochi might appear under:</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {["Mangroves", "Trees & Forests", "Oceans & Shores", "Habitat", "Conservation", "Kerala", "Ernakulam", "Kochi"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white/70 px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#151312]">
                <MapPin size={10} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />{t}
              </span>
            ))}
          </div>
          <p className="mt-4">This makes the same observation useful to many different people.</p>
        </Section>

        <Section id="neighborhood" title="From Your Neighborhood to the World">
          <p>NaturePulse can begin right outside your door.</p>
          <p>Observe your:</p>
          <List items={["Backyard", "Garden", "Street", "Neighborhood", "Local park", "Farm", "School", "Lake", "River", "Forest", "Beach", "Mountain", "Village", "Town", "City"]} />
          <p className="mt-3">Then connect those observations to the wider world.</p>
          <p>Over time, thousands of local observations can help us see regional and even global patterns.</p>
        </Section>

        <Section id="ordinary" title="Today's Ordinary Photo May Become Tomorrow's Important Record">
          <ul className="mt-2 space-y-1.5 list-none">
            <li>Take a photograph of the same tree every month.</li>
            <li>Photograph the same river every season.</li>
            <li>Photograph the shoreline from the same location every year.</li>
            <li>Record when the first butterflies arrive.</li>
            <li>Notice when a familiar bird begins nesting.</li>
            <li>Record when a fruit tree flowers.</li>
            <li>Document when a pond dries.</li>
          </ul>
          <p>These simple observations become increasingly valuable with time.</p>
          <Callout>A single photograph captures a moment. Repeated observations capture change.</Callout>
        </Section>

        <Section id="follow" title="Follow What Matters to You">
          <p>NaturePulse users should be able to personalize their Pulse.</p>
          <p>Follow:</p>
          <List items={["🌳 Trees", "🐦 Birds", "🐝 Pollinators", "🐘 Wildlife", "🌊 Oceans", "💧 Rivers", "🌦️ Weather", "🔥 Wildfires", "🌾 Agriculture", "🔬 Research"]} />
          <p className="mt-3">Or follow a particular:</p>
          <List items={["Species", "Location", "River", "Lake", "Forest", "Mountain", "Beach", "Country", "State", "City", "Village", "Expert", "Research topic"]} />
          <p className="mt-3">Your Pulse can gradually become a personalized window into the parts of nature you care about most.</p>
        </Section>

        <Section id="near-you" title="Discover What Is Happening Near You">
          <p>Location can make NaturePulse especially useful.</p>
          <p>A user could view <em>Near Me</em> and discover recent:</p>
          <List items={["Bird sightings", "Wildlife observations", "Flowering plants", "Weather events", "Flooding", "Wildfires", "Environmental warnings", "Research projects", "Nature photographs", "Community discussions"]} />
          <p className="mt-3">The global NaturePulse becomes locally meaningful.</p>
        </Section>

        <Section id="experts" title="Hear From NaturePulse Experts">
          <p>Some NaturePulse contributors may be scientists, researchers, naturalists, farmers, foresters, birdwatchers, photographers, fishermen, conservationists and other experienced observers.</p>
          <p>Their contributions can appear within the Pulse alongside community observations.</p>
          <p className="mt-3">Look for <strong>NaturePulse Expert</strong> and <strong>Verified Expert</strong> profiles.</p>
          <p className="mt-3">Experts may contribute:</p>
          <List items={["Articles", "Research explanations", "Field reports", "Useful resources", "Observations", "Answers to questions", "Conservation projects"]} />
          <p className="mt-3">The goal is not to separate experts from the community, but to bring different kinds of knowledge together.</p>
          <div className="mt-4">
            <Link
              to="/experts"
              data-testid="feel-the-pulse-experts-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white/70 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#151312] hover:bg-white"
            >
              Meet the experts <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        </Section>

        <Section id="research" title="Follow the Research">
          <p>NaturePulse also connects observations with science.</p>
          <ul className="mt-2 space-y-1.5 list-none">
            <li>A user noticing earlier flowering may discover research about changing seasonal patterns.</li>
            <li>Someone documenting coastal flooding may find studies about sea-level rise.</li>
            <li>A bird observation may connect with migration research.</li>
            <li>A photo of coral bleaching may lead to research about ocean warming.</li>
          </ul>
          <Callout>NaturePulse helps connect <em>what people observe</em> with <em>what scientists are studying.</em></Callout>
        </Section>

        <Section id="news" title="Follow the News — Then Visit the Source">
          <p>NaturePulse can also surface environmental news and research from reputable organizations around the world.</p>
          <p>Rather than simply copying external stories, NaturePulse provides a brief introduction and directs readers to the original publisher.</p>
          <p className="mt-3">You may see links from:</p>
          <List items={["Government agencies", "Universities", "Research institutes", "Scientific journals", "Environmental organizations", "Local newspapers", "International news organizations"]} />
          <Callout>Discover it on NaturePulse. Learn more from the original source.</Callout>
        </Section>

        <Section id="celebrate" title="Celebrate Nature Too">
          <p>NaturePulse should not become a stream consisting only of disasters and environmental problems.</p>
          <p>Nature is also extraordinary, beautiful and endlessly surprising.</p>
          <p className="mt-3">Share:</p>
          <List items={[
            "🌸 First flowers of spring",
            "🌈 Rainbows",
            "🦋 Butterflies",
            "🐦 Newly hatched birds",
            "🌅 Sunrises",
            "🌄 Landscapes",
            "🌙 The moon",
            "🌳 Ancient trees",
            "🐬 Wildlife encounters",
            "🌻 Successful habitat restoration",
            "🌱 Trees planted by communities",
            "🐢 Species returning to restored habitats",
          ]} />
          <p className="mt-3">NaturePulse should document both what we risk losing and what is worth protecting.</p>
        </Section>

        <Section id="missing" title="Notice What Is Missing">
          <p>Observation isn't only about seeing something.</p>
          <p>Sometimes absence matters.</p>
          <p className="mt-3">Perhaps:</p>
          <ul className="mt-2 space-y-1.5 list-none">
            <li>There are fewer butterflies than last year.</li>
            <li>A familiar bird hasn't returned.</li>
            <li>A seasonal stream no longer flows.</li>
            <li>Wildflowers have disappeared from a field.</li>
            <li>A lake is much lower than you remember.</li>
            <li>Snow arrives later.</li>
            <li>Fruit ripens earlier.</li>
          </ul>
          <p>These observations may not prove why something changed, but they can generate valuable questions.</p>
          <p className="mt-3">Record them. Compare them. Watch what happens next.</p>
        </Section>

        <Section id="ask" title="Ask Questions">
          <p>Curiosity is one of the foundations of NaturePulse.</p>
          <p>Ask:</p>
          <ul className="mt-2 space-y-1.5 list-none">
            <li>What plant is this?</li>
            <li>Why are these leaves turning brown?</li>
            <li>Why are there fewer bees this year?</li>
            <li>What bird built this nest?</li>
            <li>Why did this lake suddenly turn green?</li>
            <li>Is this an invasive species?</li>
            <li>Why is this shoreline disappearing?</li>
            <li>Are these unusual clouds?</li>
            <li>Why are animals appearing in my neighborhood?</li>
          </ul>
          <p>Questions can lead to discussion, expert responses and deeper research.</p>
        </Section>

        <Section id="respect" title="Respect Nature While Observing">
          <p>NaturePulse encourages responsible observation.</p>
          <p className="mt-3">Please:</p>
          <ul className="mt-2 space-y-1.5 list-none">
            <li>Keep a safe distance from wildlife.</li>
            <li>Never disturb nests.</li>
            <li>Do not remove rare plants.</li>
            <li>Avoid damaging habitat for a photograph.</li>
            <li>Do not feed dangerous wildlife.</li>
            <li>Respect private property.</li>
            <li>Follow park and conservation rules.</li>
            <li>Never enter floodwater, wildfire zones or other hazardous areas to create a post.</li>
          </ul>
          <Callout>Your safety — and the safety of wildlife — comes first.</Callout>
        </Section>

        <Section id="living-record" title="A Living Record of Our Planet">
          <p>Imagine NaturePulse years from now.</p>
          <p>Millions of observations could collectively document:</p>
          <List items={[
            "When trees flowered",
            "Where birds migrated",
            "How shorelines changed",
            "Where flooding occurred",
            "When snow arrived",
            "When lakes froze and thawed",
            "Where wildlife appeared",
            "How forests recovered after fires",
            "When crops were harvested",
            "Which species appeared or disappeared",
            "How communities restored damaged ecosystems",
          ]} />
          <p className="mt-3">Each individual observation may be modest.</p>
          <Callout>Together they can create something extraordinary: a living record of our changing natural world.</Callout>
        </Section>

        <Section id="add-your-pulse" title="Add Your Pulse">
          <p>Look around you.</p>
          <p>Something is happening in nature right now.</p>
          <ul className="mt-2 space-y-1.5 list-none">
            <li>A bird may be calling.</li>
            <li>A tree may be flowering.</li>
            <li>Clouds may be forming.</li>
            <li>Rain may be approaching.</li>
            <li>An insect may be pollinating a flower.</li>
            <li>A river may be rising.</li>
            <li>A season may quietly be changing.</li>
          </ul>
          <p className="mt-4 font-serif-display text-2xl text-[#151312] leading-tight">
            Notice it. Photograph it. Record it. Share it.
          </p>
          <p>And help others understand what nature is telling us.</p>
        </Section>

        {/* Closing */}
        <div className="mt-14 md:mt-16 rounded-2xl bg-gradient-to-br from-[#151312] via-[#1E1A16] to-[#241A10] text-[#F5EBDC] p-8 md:p-12 text-center">
          <div className="font-mono-tech text-[10px] tracking-[0.36em] uppercase text-[#E4A15C] mb-2 inline-flex items-center gap-2">
            <Sparkle size={11} weight="fill" /> NaturePulse
          </div>
          <h3 className="font-serif-display text-3xl md:text-5xl leading-tight">Observe · Discover · Share · Understand</h3>
          <p className="mt-4 text-[#E5D8C4]/85 text-base md:text-lg">Add your Pulse to the story of our planet.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/stories/new"
              data-testid="feel-the-pulse-closing-add"
              className="inline-flex items-center gap-2 rounded-full bg-[#E4A15C] text-[#151312] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#F5EBDC]"
            >
              Add a Pulse <ArrowRight size={12} weight="bold" />
            </Link>
            <Link
              to="/observations"
              data-testid="feel-the-pulse-closing-browse"
              className="inline-flex items-center gap-2 rounded-full border border-[#E4A15C]/40 text-[#E5D8C4] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-white/5"
            >
              Browse the Pulse
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
